/**
 * JobCrawls SEO Meta Generator v2
 *
 * Generates unique, indexable <title>, <meta description>, and robots
 * directives for search / filter pages in EN, FI and SV.
 *
 * v2 changes:
 *   - Proper Finnish grammar: city inessive/adessive cases, field partitive
 *   - Swedish "i/på" preposition handling for cities
 *   - Guaranteed minimum description length (≥140 chars) via fallback facts
 *   - Richer fallback content when data points are sparse
 */

import type {
  SRPLanguage,
  FilterType,
  ActiveFilter,
  AggregatedJobData,
  SEOMeta,
} from '../../types/srp-seo-meta'

export type { SRPLanguage, FilterType, ActiveFilter, AggregatedJobData, SEOMeta }

// ============================================================================
// CONSTANTS
// ============================================================================

const FILTER_PRIORITY: FilterType[] = [
  'location', 'company', 'field', 'remoteness', 'requiredLanguages',
  'jobType', 'normalisedRoleLevel', 'companyIndustry', 'specialisations',
  'educationLevel', 'benefits', 'skillsAndTechnologies', 'peopleManagementTier',
]

const NO_INDEX_ALONE: FilterType[] = [
  'educationLevel', 'benefits', 'skillsAndTechnologies',
  'peopleManagementTier', 'specialisations',
]

const MIN_JOBS_FOR_INDEX = 5
const MIN_DESCRIPTION_LENGTH = 140
const MAX_DESCRIPTION_LENGTH = 160
const MAX_TITLE_LENGTH = 65

// ============================================================================
// FINNISH CITY GRAMMAR
// ============================================================================

/**
 * Finnish cities use either the INESSIVE case (-ssa/-ssä) or the
 * ADESSIVE case (-lla/-llä) for "in [city]". There is no reliable rule;
 * it must be learned per city.
 *
 * This table covers the cities in the sitemap. Add new cities as you expand.
 * The key is the canonical city name (before the underscore in your URL
 * pattern, e.g. "Helsinki" from "Helsinki_Finland").
 */
interface FinnishCityForm {
  inessive: string    // Helsingissä, Espoossa, Turussa, Tampereella
  elative: string     // Helsingistä, Espoosta, Turusta, Tampereelta
  short?: string      // display name override
}

const FINNISH_CITY_FORMS: Record<string, FinnishCityForm> = {
  'Helsinki':       { inessive: 'Helsingissä',       elative: 'Helsingistä' },
  'Espoo':          { inessive: 'Espoossa',          elative: 'Espoosta' },
  'Vantaa':         { inessive: 'Vantaalla',         elative: 'Vantaalta' },
  'Tampere':        { inessive: 'Tampereella',        elative: 'Tampereelta' },
  'Turku':          { inessive: 'Turussa',           elative: 'Turusta' },
  'Oulu':           { inessive: 'Oulussa',           elative: 'Oulusta' },
  'Lahti':          { inessive: 'Lahdessa',          elative: 'Lahdesta' },
  'Kuopio':         { inessive: 'Kuopiossa',         elative: 'Kuopiosta' },
  'Jyväskylä':      { inessive: 'Jyväskylässä',      elative: 'Jyväskylästä' },
  'Pori':           { inessive: 'Porissa',           elative: 'Porista' },
  'Lappeenranta':   { inessive: 'Lappeenrannassa',   elative: 'Lappeenrannasta' },
  'Kotka':          { inessive: 'Kotkassa',          elative: 'Kotkasta' },
  'Joensuu':        { inessive: 'Joensuussa',        elative: 'Joensuusta' },
  'Rovaniemi':      { inessive: 'Rovaniemellä',      elative: 'Rovaniemeltä' },
  'Seinäjoki':      { inessive: 'Seinäjoella',       elative: 'Seinäjoelta' },
  'Vaasa':          { inessive: 'Vaasassa',          elative: 'Vaasasta' },
  'Porvoo':         { inessive: 'Porvoossa',         elative: 'Porvoosta' },
  'Hämeenlinna':    { inessive: 'Hämeenlinnassa',    elative: 'Hämeenlinnasta' },
  'Mikkeli':        { inessive: 'Mikkelissä',        elative: 'Mikkelistä' },
  'Kokkola':        { inessive: 'Kokkolassa',        elative: 'Kokkolasta' },
  'Kajaani':        { inessive: 'Kajaanissa',        elative: 'Kajaanista' },
  'Rauma':          { inessive: 'Raumalla',          elative: 'Raumalta' },
  'Lohja':          { inessive: 'Lohjalla',          elative: 'Lohjalta' },
  'Nurmijärvi':     { inessive: 'Nurmijärvellä',     elative: 'Nurmijärveltä' },
  'Hyvinkää':       { inessive: 'Hyvinkäällä',       elative: 'Hyvinkäältä' },
  'Kemi':           { inessive: 'Kemissä',           elative: 'Kemistä' },
  'Pietarsaari':    { inessive: 'Pietarsaaressa',    elative: 'Pietarsaaresta' },
  'Valkeakoski':    { inessive: 'Valkeakoskella',    elative: 'Valkeakoskelta' },
  'Kaskinen':       { inessive: 'Kaskisissa',        elative: 'Kaskisista' },
}

/**
 * Get the Finnish locative form of a city name ("in Helsinki" → "Helsingissä").
 *
 * Falls back to the safe prepositional form "sijainnissa [City]" for unknown
 * cities, which is always grammatically correct (if slightly formal).
 */
export function getCityInessive(cityName: string): string {
  const city = cityName.split(/[,_]/)[0].trim()
  const form = FINNISH_CITY_FORMS[city]
  if (form) return form.inessive

  if (typeof console !== 'undefined') {
    console.warn(
      `[SEO] Missing Finnish locative for city: "${city}" — ` +
      `add it to FINNISH_CITY_FORMS. Using safe fallback "sijainnissa ${city}".`
    )
  }
  return `sijainnissa ${city}`
}

/**
 * Finnish field names in the genitive case for "[Field]-alan työpaikat".
 */
const FINNISH_FIELD_GENITIVE: Record<string, string> = {
  'Terveys ja hyvinvointi': 'Terveys- ja hyvinvointialan',
  'Teknologia': 'Teknologia-alan',
  'Terveydenhuolto': 'Terveydenhuoltoalan',
  'Asiakaspalvelu': 'Asiakaspalvelualan',
  'Myynti': 'Myyntialan',
  'Rahoitus': 'Rahoitusalan',
  'Koulutus': 'Koulutusalan',
  'Valmistus': 'Valmistusalan',
  'Suunnittelu': 'Suunnittelualan',
  'Hallinto': 'Hallintoalan',
  'Henkilöstöhallinto': 'Henkilöstöhallintoalan',
  'Markkinointi': 'Markkinointialan',
}

export function getFieldGenitive(fieldName: string): string {
  return FINNISH_FIELD_GENITIVE[fieldName] || `${fieldName}-alan`
}

// ============================================================================
// SWEDISH CITY GRAMMAR
// ============================================================================

interface SwedishCityForm {
  prep: 'i' | 'på'
  name?: string
}

const SWEDISH_CITY_FORMS: Record<string, SwedishCityForm> = {
  'Helsinki':     { prep: 'i', name: 'Helsingfors' },
  'Turku':        { prep: 'i', name: 'Åbo' },
  'Tampere':      { prep: 'i', name: 'Tammerfors' },
  'Vaasa':        { prep: 'i', name: 'Vasa' },
  'Porvoo':       { prep: 'i', name: 'Borgå' },
  'Pietarsaari':  { prep: 'i', name: 'Jakobstad' },
  'Espoo':        { prep: 'i', name: 'Esbo' },
  'Vantaa':       { prep: 'i', name: 'Vanda' },
  'Oulu':         { prep: 'i', name: 'Uleåborg' },
  'Kotka':        { prep: 'i' },
  'Kuopio':       { prep: 'i' },
  'Jyväskylä':    { prep: 'i' },
  'Lahti':        { prep: 'i', name: 'Lahtis' },
  'Hämeenlinna':  { prep: 'i', name: 'Tavastehus' },
  'Lappeenranta': { prep: 'i', name: 'Villmanstrand' },
  'Joensuu':      { prep: 'i' },
  'Rovaniemi':    { prep: 'i' },
  'Kokkola':      { prep: 'i', name: 'Karleby' },
  'Mikkeli':      { prep: 'i', name: 'S:t Michel' },
  'Kajaani':      { prep: 'i', name: 'Kajana' },
  'Pori':         { prep: 'i', name: 'Björneborg' },
  'Seinäjoki':    { prep: 'i' },
  'Rauma':        { prep: 'i', name: 'Raumo' },
  'Lohja':        { prep: 'i', name: 'Lojo' },
  'Hyvinkää':     { prep: 'i', name: 'Hyvinge' },
  'Nurmijärvi':   { prep: 'i' },
}

export function getSwedishCityPrep(cityName: string): string {
  const city = cityName.split(/[,_]/)[0].trim()
  const form = SWEDISH_CITY_FORMS[city]
  const name = form?.name || city
  const prep = form?.prep || 'i'
  return `${prep} ${name}`
}

export function getSwedishCityName(cityName: string): string {
  const city = cityName.split(/[,_]/)[0].trim()
  const form = SWEDISH_CITY_FORMS[city]
  return form?.name || city
}

// ============================================================================
// FINNISH LANGUAGE-REQUIREMENT FORMS
// ============================================================================

const FINNISH_LANGUAGE_ADJECTIVE: Record<string, string> = {
  'englanti': 'englanninkieliset',
  'suomi': 'suomenkieliset',
  'ruotsi': 'ruotsinkieliset',
  'saksa': 'saksankieliset',
  'ranska': 'ranskankieliset',
  'espanja': 'espanjankieliset',
  'venäjä': 'venäjänkieliset',
}

export function getFinnishLanguageAdjective(langValue: string): string {
  const lower = langValue.toLowerCase()
  return FINNISH_LANGUAGE_ADJECTIVE[lower] || `${langValue}kieliset`
}

const SWEDISH_LANGUAGE_ADJECTIVE: Record<string, string> = {
  'english': 'engelskspråkiga',
  'finnish': 'finskspråkiga',
  'swedish': 'svenskspråkiga',
  'german': 'tyskspråkiga',
  'engelska': 'engelskspråkiga',
  'finska': 'finskspråkiga',
  'svenska': 'svenskspråkiga',
}

export function getSwedishLanguageAdjective(langValue: string): string {
  const lower = langValue.toLowerCase()
  return SWEDISH_LANGUAGE_ADJECTIVE[lower] || `${langValue}språkiga`
}

// ============================================================================
// MAIN ENTRY POINT
// ============================================================================

export function generateSEOMeta(
  activeFilters: ActiveFilter[],
  jobData: AggregatedJobData,
  lang: SRPLanguage,
  pageUrl: string,
): SEOMeta {
  const robots = getRobotsDirective(activeFilters, jobData.count)
  const title = generateTitle(activeFilters, jobData.count, lang)
  const description = generateDescription(activeFilters, jobData, lang)

  return { title, description, robots, canonical: pageUrl }
}

// ============================================================================
// ROBOTS DIRECTIVE
// ============================================================================

export function getRobotsDirective(filters: ActiveFilter[], count: number | null): string {
  if (count !== null && count < MIN_JOBS_FOR_INDEX) return 'noindex, follow'
  if (filters.length === 0) return 'index, follow'
  if (filters.length >= 3) return 'noindex, follow'

  if (filters.length === 1 && NO_INDEX_ALONE.includes(filters[0].type)) {
    return 'noindex, follow'
  }

  if (filters.length === 2) {
    const high: FilterType[] = ['location', 'company', 'field', 'remoteness', 'requiredLanguages']
    if (!filters.some(f => high.includes(f.type))) return 'noindex, follow'
  }

  return 'index, follow'
}

// ============================================================================
// TITLE GENERATION
// ============================================================================

export function generateTitle(filters: ActiveFilter[], count: number | null, lang: SRPLanguage): string {
  const sorted = sortByPriority(filters)
  const p = sorted[0] || null
  const s = sorted[1] || null
  const c = count != null && count > 0 ? fmtCount(count) : null

  if (!p) return homepageTitle(c, lang)
  if (!s) return singleFilterTitle(p, c, lang)
  return twoFilterTitle(p, s, c, lang)
}

function homepageTitle(count: string | null, lang: SRPLanguage): string {
  switch (lang) {
    case 'en': return count
      ? trunc(`Jobs in Finland – ${count} Open Positions | JobCrawls`)
      : trunc(`Jobs in Finland – Open Positions | JobCrawls`)
    case 'fi': return count
      ? trunc(`Työpaikat Suomessa – ${count} avointa paikkaa | JobCrawls`)
      : trunc(`Työpaikat Suomessa – avoimia paikkoja | JobCrawls`)
    case 'sv': return count
      ? trunc(`Jobb i Finland – ${count} lediga tjänster | JobCrawls`)
      : trunc(`Jobb i Finland – lediga tjänster | JobCrawls`)
  }
}

function singleFilterTitle(f: ActiveFilter, count: string | null, lang: SRPLanguage): string {
  switch (lang) {
    case 'en': return singleTitleEN(f, count)
    case 'fi': return singleTitleFI(f, count)
    case 'sv': return singleTitleSV(f, count)
  }
}

function singleTitleEN(f: ActiveFilter, c: string | null): string {
  const v = f.value
  const n = c ? `${c} ` : ''
  switch (f.type) {
    case 'location':            return trunc(`Jobs in ${v} – ${n}Open Positions | JobCrawls`)
    case 'company':             return trunc(`${v} Jobs – ${n}Open Positions | JobCrawls`)
    case 'field':               return trunc(`${v} Jobs in Finland – ${n}Positions | JobCrawls`)
    case 'companyIndustry':     return trunc(`${v} Industry Jobs in Finland${c ? ` – ${c}` : ''} | JobCrawls`)
    case 'remoteness':
      return f.rawValue.toLowerCase() === 'remote'
        ? trunc(`Remote Jobs in Finland – ${n}Positions | JobCrawls`)
        : trunc(`${v} Jobs in Finland – ${n}Positions | JobCrawls`)
    case 'requiredLanguages':   return trunc(`${capitalize(v)}-Speaking Jobs in Finland${c ? ` – ${c}` : ''} | JobCrawls`)
    case 'jobType':             return trunc(`${v} Jobs in Finland – ${n}Positions | JobCrawls`)
    case 'normalisedRoleLevel': return trunc(`${v}-Level Jobs in Finland${c ? ` – ${c}` : ''} | JobCrawls`)
    default:                    return trunc(`Jobs in Finland – ${n}Positions | JobCrawls`)
  }
}

function singleTitleFI(f: ActiveFilter, c: string | null): string {
  const v = f.value
  const dash = c ? ` – ${c}` : ''
  switch (f.type) {
    case 'location': {
      const loc = getCityInessive(f.rawValue)
      return trunc(`Työpaikat ${loc}${c ? ` – ${c} avointa paikkaa` : ''} | JobCrawls`)
    }
    case 'company':
      return trunc(`${v} avoimet työpaikat${c ? ` – ${c} paikkaa` : ''} | JobCrawls`)
    case 'field': {
      const gen = getFieldGenitive(v)
      return trunc(`${gen} työpaikat Suomessa${c ? ` – ${c} paikkaa` : ''} | JobCrawls`)
    }
    case 'companyIndustry': {
      const gen = getFieldGenitive(v)
      return trunc(`${gen} työpaikat Suomessa${dash} | JobCrawls`)
    }
    case 'remoteness':
      return f.rawValue.toLowerCase() === 'remote'
        ? trunc(`Etätyöpaikat Suomessa${c ? ` – ${c} paikkaa` : ''} | JobCrawls`)
        : trunc(`${v} Suomessa${c ? ` – ${c} paikkaa` : ''} | JobCrawls`)
    case 'requiredLanguages': {
      const adj = getFinnishLanguageAdjective(v)
      return trunc(`${capitalize(adj)} työpaikat Suomessa${dash} | JobCrawls`)
    }
    case 'jobType':
      return trunc(`${v} työpaikat Suomessa${c ? ` – ${c} paikkaa` : ''} | JobCrawls`)
    case 'normalisedRoleLevel':
      return trunc(`${v}-tason työpaikat Suomessa${dash} | JobCrawls`)
    default:
      return trunc(`Työpaikat Suomessa${c ? ` – ${c} paikkaa` : ''} | JobCrawls`)
  }
}

function singleTitleSV(f: ActiveFilter, c: string | null): string {
  const v = f.value
  const dash = c ? ` – ${c}` : ''
  switch (f.type) {
    case 'location': {
      const loc = getSwedishCityPrep(f.rawValue)
      return trunc(`Jobb ${loc}${c ? ` – ${c} lediga tjänster` : ''} | JobCrawls`)
    }
    case 'company':
      return trunc(`${v} lediga jobb${c ? ` – ${c} tjänster` : ''} | JobCrawls`)
    case 'field':
      return trunc(`${v}-jobb i Finland${c ? ` – ${c} tjänster` : ''} | JobCrawls`)
    case 'companyIndustry':
      return trunc(`${v}-jobb i Finland${dash} | JobCrawls`)
    case 'remoteness':
      return f.rawValue.toLowerCase() === 'remote'
        ? trunc(`Distansjobb i Finland${c ? ` – ${c} tjänster` : ''} | JobCrawls`)
        : trunc(`${v}-jobb i Finland${c ? ` – ${c} tjänster` : ''} | JobCrawls`)
    case 'requiredLanguages': {
      const adj = getSwedishLanguageAdjective(v)
      return trunc(`${capitalize(adj)} jobb i Finland${dash} | JobCrawls`)
    }
    case 'jobType':
      return trunc(`${v} jobb i Finland${c ? ` – ${c} tjänster` : ''} | JobCrawls`)
    case 'normalisedRoleLevel':
      return trunc(`${v}-jobb i Finland${c ? ` – ${c} tjänster` : ''} | JobCrawls`)
    default:
      return trunc(`Jobb i Finland${c ? ` – ${c} tjänster` : ''} | JobCrawls`)
  }
}

function twoFilterTitle(
  p: ActiveFilter, s: ActiveFilter, c: string | null, lang: SRPLanguage,
): string {
  switch (lang) {
    case 'en': return twoFilterTitleEN(p, s, c)
    case 'fi': return twoFilterTitleFI(p, s, c)
    case 'sv': return twoFilterTitleSV(p, s, c)
  }
}

function twoFilterTitleEN(p: ActiveFilter, s: ActiveFilter, c: string | null): string {
  const dash = c ? ` – ${c}` : ''
  if (p.type === 'location') {
    switch (s.type) {
      case 'field':
      case 'companyIndustry':     return trunc(`${s.value} Jobs in ${p.value}${dash} | JobCrawls`)
      case 'requiredLanguages':   return trunc(`${capitalize(s.value)}-Speaking Jobs in ${p.value}${dash} | JobCrawls`)
      case 'remoteness':          return trunc(`Remote Jobs in ${p.value}${dash} | JobCrawls`)
      case 'company':             return trunc(`${s.value} Jobs in ${p.value}${dash} | JobCrawls`)
      case 'jobType':             return trunc(`${s.value} Jobs in ${p.value}${dash} | JobCrawls`)
      case 'normalisedRoleLevel': return trunc(`${s.value} Jobs in ${p.value}${dash} | JobCrawls`)
      default:                    return trunc(`Jobs in ${p.value}${c ? ` – ${c} Open Positions` : ''} | JobCrawls`)
    }
  }
  if (p.type === 'company') {
    switch (s.type) {
      case 'location': return trunc(`${p.value} Jobs in ${s.value}${dash} | JobCrawls`)
      case 'field':    return trunc(`${p.value} ${s.value} Jobs${dash} | JobCrawls`)
      default:         return trunc(`${p.value} Jobs${c ? ` – ${c} Open Positions` : ''} | JobCrawls`)
    }
  }
  if (p.type === 'field') {
    switch (s.type) {
      case 'location':          return trunc(`${p.value} Jobs in ${s.value}${dash} | JobCrawls`)
      case 'remoteness':        return trunc(`Remote ${p.value} Jobs in Finland${dash} | JobCrawls`)
      case 'requiredLanguages': return trunc(`${capitalize(s.value)}-Speaking ${p.value} Jobs in Finland${dash} | JobCrawls`)
      default:                  return trunc(`${p.value} Jobs in Finland${dash} | JobCrawls`)
    }
  }
  if (p.type === 'remoteness') {
    const isRemote = p.rawValue.toLowerCase() === 'remote'
    const lbl = isRemote ? 'Remote' : capitalize(p.value)
    switch (s.type) {
      case 'requiredLanguages':   return trunc(`${capitalize(s.value)}-Speaking ${lbl} Jobs in Finland${dash} | JobCrawls`)
      case 'field':               return trunc(`${lbl} ${s.value} Jobs in Finland${dash} | JobCrawls`)
      case 'jobType':             return trunc(`${lbl} ${capitalize(s.value)} Jobs in Finland${dash} | JobCrawls`)
      case 'normalisedRoleLevel': return trunc(`${lbl} ${s.value}-Level Jobs in Finland${dash} | JobCrawls`)
      case 'companyIndustry':     return trunc(`${lbl} ${s.value} Industry Jobs in Finland${dash} | JobCrawls`)
      default:                    return trunc(`${lbl} Jobs in Finland${dash} | JobCrawls`)
    }
  }
  if (p.type === 'requiredLanguages') {
    const spk = `${capitalize(p.value)}-Speaking`
    switch (s.type) {
      case 'remoteness': {
        const isRemote = s.rawValue.toLowerCase() === 'remote'
        return trunc(`${spk} ${isRemote ? 'Remote' : capitalize(s.value)} Jobs in Finland${dash} | JobCrawls`)
      }
      case 'field':               return trunc(`${spk} ${s.value} Jobs in Finland${dash} | JobCrawls`)
      case 'jobType':             return trunc(`${spk} ${capitalize(s.value)} Jobs in Finland${dash} | JobCrawls`)
      case 'normalisedRoleLevel': return trunc(`${spk} ${s.value}-Level Jobs in Finland${dash} | JobCrawls`)
      case 'companyIndustry':     return trunc(`${spk} ${s.value} Industry Jobs in Finland${dash} | JobCrawls`)
      default:                    return trunc(`${spk} Jobs in Finland${dash} | JobCrawls`)
    }
  }
  return trunc(`${capitalize(p.value)} ${s.value} Jobs in Finland${dash} | JobCrawls`)
}

function twoFilterTitleFI(p: ActiveFilter, s: ActiveFilter, c: string | null): string {
  const dash = c ? ` – ${c}` : ''
  if (p.type === 'location') {
    const loc = getCityInessive(p.rawValue)
    switch (s.type) {
      case 'field':
      case 'companyIndustry': {
        const gen = getFieldGenitive(s.value)
        return trunc(`${gen} työpaikat ${loc}${c ? ` – ${c} paikkaa` : ''} | JobCrawls`)
      }
      case 'requiredLanguages': {
        const adj = getFinnishLanguageAdjective(s.value)
        return trunc(`${capitalize(adj)} työpaikat ${loc}${dash} | JobCrawls`)
      }
      case 'remoteness':
        return trunc(`Etätyöpaikat ${loc}${c ? ` – ${c} paikkaa` : ''} | JobCrawls`)
      case 'company':
        return trunc(`${s.value} työpaikat ${loc}${c ? ` – ${c} paikkaa` : ''} | JobCrawls`)
      case 'jobType':
        return trunc(`${s.value} työpaikat ${loc}${c ? ` – ${c} paikkaa` : ''} | JobCrawls`)
      case 'normalisedRoleLevel':
        return trunc(`${s.value}-tason työpaikat ${loc}${dash} | JobCrawls`)
      default:
        return trunc(`Työpaikat ${loc}${c ? ` – ${c} avointa paikkaa` : ''} | JobCrawls`)
    }
  }
  if (p.type === 'company') {
    switch (s.type) {
      case 'location': {
        const loc = getCityInessive(s.rawValue)
        return trunc(`${p.value} työpaikat ${loc}${c ? ` – ${c} paikkaa` : ''} | JobCrawls`)
      }
      case 'field': {
        const gen = getFieldGenitive(s.value)
        return trunc(`${p.value} ${gen} työpaikat${dash} | JobCrawls`)
      }
      default:
        return trunc(`${p.value} avoimet työpaikat${c ? ` – ${c} paikkaa` : ''} | JobCrawls`)
    }
  }
  if (p.type === 'field') {
    const gen = getFieldGenitive(p.value)
    switch (s.type) {
      case 'location': {
        const loc = getCityInessive(s.rawValue)
        return trunc(`${gen} työpaikat ${loc}${c ? ` – ${c} paikkaa` : ''} | JobCrawls`)
      }
      case 'remoteness':
        return trunc(`${gen} etätyöpaikat Suomessa${dash} | JobCrawls`)
      case 'requiredLanguages': {
        const adj = getFinnishLanguageAdjective(s.value)
        return trunc(`${capitalize(adj)} ${gen.toLowerCase()} työpaikat${dash} | JobCrawls`)
      }
      default:
        return trunc(`${gen} työpaikat Suomessa${c ? ` – ${c} paikkaa` : ''} | JobCrawls`)
    }
  }
  if (p.type === 'remoteness') {
    const isRemote = p.rawValue.toLowerCase() === 'remote' || p.rawValue.toLowerCase() === 'etätyö'
    switch (s.type) {
      case 'requiredLanguages': {
        const adj = getFinnishLanguageAdjective(s.value)
        return isRemote
          ? trunc(`${capitalize(adj)} etätyöpaikat Suomessa${dash} | JobCrawls`)
          : trunc(`${capitalize(adj)} ${p.value} työpaikat Suomessa${dash} | JobCrawls`)
      }
      case 'field': {
        const gen = getFieldGenitive(s.value)
        return isRemote
          ? trunc(`${gen} etätyöpaikat Suomessa${c ? ` – ${c} paikkaa` : ''} | JobCrawls`)
          : trunc(`${gen} ${p.value.toLowerCase()} työpaikat Suomessa${c ? ` – ${c} paikkaa` : ''} | JobCrawls`)
      }
      default:
        return isRemote
          ? trunc(`Etätyöpaikat Suomessa${c ? ` – ${c} paikkaa` : ''} | JobCrawls`)
          : trunc(`${capitalize(p.value)} Suomessa${c ? ` – ${c} paikkaa` : ''} | JobCrawls`)
    }
  }
  if (p.type === 'requiredLanguages') {
    const adj = getFinnishLanguageAdjective(p.value)
    switch (s.type) {
      case 'remoteness': {
        const isRemote = s.rawValue.toLowerCase() === 'remote' || s.rawValue.toLowerCase() === 'etätyö'
        return isRemote
          ? trunc(`${capitalize(adj)} etätyöpaikat Suomessa${dash} | JobCrawls`)
          : trunc(`${capitalize(adj)} ${s.value} työpaikat Suomessa${dash} | JobCrawls`)
      }
      case 'field': {
        const gen = getFieldGenitive(s.value)
        return trunc(`${capitalize(adj)} ${gen.toLowerCase()} työpaikat${dash} | JobCrawls`)
      }
      default:
        return trunc(`${capitalize(adj)} työpaikat Suomessa${dash} | JobCrawls`)
    }
  }
  return trunc(`${capitalize(p.value)} ${s.value} työpaikat Suomessa${c ? ` – ${c} paikkaa` : ''} | JobCrawls`)
}

function twoFilterTitleSV(p: ActiveFilter, s: ActiveFilter, c: string | null): string {
  const dash = c ? ` – ${c}` : ''
  if (p.type === 'location') {
    const loc = getSwedishCityPrep(p.rawValue)
    switch (s.type) {
      case 'field':
      case 'companyIndustry':     return trunc(`${s.value}-jobb ${loc}${c ? ` – ${c} tjänster` : ''} | JobCrawls`)
      case 'requiredLanguages': {
        const adj = getSwedishLanguageAdjective(s.value)
        return trunc(`${capitalize(adj)} jobb ${loc}${dash} | JobCrawls`)
      }
      case 'remoteness':          return trunc(`Distansjobb ${loc}${c ? ` – ${c} tjänster` : ''} | JobCrawls`)
      case 'company':             return trunc(`${s.value}-jobb ${loc}${c ? ` – ${c} tjänster` : ''} | JobCrawls`)
      case 'jobType':             return trunc(`${s.value} jobb ${loc}${c ? ` – ${c} tjänster` : ''} | JobCrawls`)
      case 'normalisedRoleLevel': return trunc(`${s.value}-jobb ${loc}${c ? ` – ${c} tjänster` : ''} | JobCrawls`)
      default:                    return trunc(`Jobb ${loc}${c ? ` – ${c} lediga tjänster` : ''} | JobCrawls`)
    }
  }
  if (p.type === 'company') {
    switch (s.type) {
      case 'location': {
        const loc = getSwedishCityPrep(s.rawValue)
        return trunc(`${p.value}-jobb ${loc}${c ? ` – ${c} tjänster` : ''} | JobCrawls`)
      }
      case 'field':
        return trunc(`${p.value} ${s.value}-jobb${c ? ` – ${c} tjänster` : ''} | JobCrawls`)
      default:
        return trunc(`${p.value} lediga jobb${c ? ` – ${c} tjänster` : ''} | JobCrawls`)
    }
  }
  if (p.type === 'field') {
    switch (s.type) {
      case 'location': {
        const loc = getSwedishCityPrep(s.rawValue)
        return trunc(`${p.value}-jobb ${loc}${c ? ` – ${c} tjänster` : ''} | JobCrawls`)
      }
      case 'remoteness':
        return trunc(`${p.value} distansjobb i Finland${dash} | JobCrawls`)
      case 'requiredLanguages': {
        const adj = getSwedishLanguageAdjective(s.value)
        return trunc(`${capitalize(adj)} ${p.value.toLowerCase()}-jobb${dash} | JobCrawls`)
      }
      default:
        return trunc(`${p.value}-jobb i Finland${c ? ` – ${c} tjänster` : ''} | JobCrawls`)
    }
  }
  if (p.type === 'remoteness') {
    const isRemote = p.rawValue.toLowerCase() === 'remote' || p.rawValue.toLowerCase() === 'distans'
    const lbl = isRemote ? 'distans' : p.value.toLowerCase()
    switch (s.type) {
      case 'requiredLanguages': {
        const adj = getSwedishLanguageAdjective(s.value)
        return trunc(`${capitalize(adj)} ${lbl}jobb i Finland${dash} | JobCrawls`)
      }
      case 'field':
        return trunc(`${s.value} ${lbl}jobb i Finland${c ? ` – ${c} tjänster` : ''} | JobCrawls`)
      default:
        return isRemote
          ? trunc(`Distansjobb i Finland${c ? ` – ${c} tjänster` : ''} | JobCrawls`)
          : trunc(`${capitalize(p.value)}-jobb i Finland${c ? ` – ${c} tjänster` : ''} | JobCrawls`)
    }
  }
  if (p.type === 'requiredLanguages') {
    const adj = getSwedishLanguageAdjective(p.value)
    switch (s.type) {
      case 'remoteness': {
        const isRemote = s.rawValue.toLowerCase() === 'remote' || s.rawValue.toLowerCase() === 'distans'
        return trunc(`${capitalize(adj)} ${isRemote ? 'distans' : s.value.toLowerCase()}jobb i Finland${dash} | JobCrawls`)
      }
      case 'field':
        return trunc(`${capitalize(adj)} ${s.value.toLowerCase()}-jobb i Finland${dash} | JobCrawls`)
      default:
        return trunc(`${capitalize(adj)} jobb i Finland${dash} | JobCrawls`)
    }
  }
  return trunc(`${capitalize(p.value)} ${s.value}-jobb i Finland${c ? ` – ${c} tjänster` : ''} | JobCrawls`)
}

// ============================================================================
// DESCRIPTION GENERATION
// ============================================================================

/**
 * Generates a meta description that is ALWAYS ≥140 characters.
 *
 * Strategy:
 * 1. Build primary facts from available data (salary, employers, etc.)
 * 2. Build secondary/fallback facts (skills, benefits, salary range, job types)
 * 3. Combine facts until we hit the 140-160 char target
 * 4. If still short, append a branded CTA
 */
export function generateDescription(
  filters: ActiveFilter[],
  data: AggregatedJobData,
  lang: SRPLanguage,
): string {
  const filterTypes = new Set(filters.map(f => f.type))
  const facts: string[] = []
  const fallbackFacts: string[] = []

  facts.push(buildOpener(filters, data.count, lang))

  if (data.avgSalary) {
    facts.push(salaryFact(data.avgSalary, lang))
  }

  if (!filterTypes.has('company') && data.topEmployers.length > 0) {
    facts.push(listFact(label('employers', lang), data.topEmployers.slice(0, 3)))
  }

  if (!filterTypes.has('location') && data.topCities.length > 0) {
    facts.push(listFact(label('cities', lang), data.topCities.slice(0, 3)))
  }

  if (!filterTypes.has('requiredLanguages') && data.englishPct != null && data.englishPct > 0) {
    facts.push(pctFact(data.englishPct, label('english', lang)))
  }

  if (!filterTypes.has('remoteness') && data.remotePct != null && data.remotePct > 0) {
    facts.push(pctFact(data.remotePct, label('remote', lang)))
  }

  if (!filterTypes.has('field') && !filterTypes.has('companyIndustry') && data.topFields.length > 0) {
    facts.push(listFact(label('fields', lang), data.topFields.slice(0, 3)))
  }

  if (!filterTypes.has('jobType') && data.fullTimePct != null) {
    fallbackFacts.push(pctFact(data.fullTimePct, label('fulltime', lang)))
  }

  if (data.salaryRangeLabel) {
    fallbackFacts.push(rangeFact(data.salaryRangeLabel, lang))
  }

  if (data.topSkills.length > 0) {
    fallbackFacts.push(listFact(label('skills', lang), data.topSkills.slice(0, 3)))
  }

  if (data.topBenefits.length > 0) {
    fallbackFacts.push(listFact(label('benefits', lang), data.topBenefits.slice(0, 3)))
  }

  const cta = ctaText(lang)

  let desc = facts.join('. ') + '.'

  let fallbackIdx = 0
  while (desc.length < MIN_DESCRIPTION_LENGTH && fallbackIdx < fallbackFacts.length) {
    desc = desc + ' ' + fallbackFacts[fallbackIdx] + '.'
    fallbackIdx++
  }

  if (desc.length < MIN_DESCRIPTION_LENGTH) {
    desc = desc + ' ' + cta
  }

  let extraIdx = 0
  const extras = extraDescriptionClauses(lang)
  while (desc.length < MIN_DESCRIPTION_LENGTH && extraIdx < extras.length) {
    desc = desc + ' ' + extras[extraIdx]
    extraIdx += 1
  }

  if (desc.length < MIN_DESCRIPTION_LENGTH) {
    desc = desc + ' ' + cta
  }

  if (desc.length > MAX_DESCRIPTION_LENGTH) {
    desc = truncDesc(desc, MAX_DESCRIPTION_LENGTH - 3)
  }

  return desc
}

function buildOpener(filters: ActiveFilter[], count: number | null, lang: SRPLanguage): string {
  const c = count != null && count > 0 ? fmtCount(count) : null
  const sorted = sortByPriority(filters)
  const p = sorted[0]

  if (!p) {
    switch (lang) {
      case 'en': return c ? `Browse ${c} open jobs in Finland` : `Browse open jobs in Finland`
      case 'fi': return c ? `Selaa ${c} avointa työpaikkaa Suomessa` : `Selaa avointa työpaikkaa Suomessa`
      case 'sv': return c ? `Bläddra bland ${c} lediga jobb i Finland` : `Bläddra bland lediga jobb i Finland`
    }
  }

  switch (lang) {
    case 'en':
      switch (p.type) {
        case 'location':
          return c ? `Browse ${c} open jobs in ${p.value}` : `Browse open jobs in ${p.value}`
        case 'company':
          return c ? `${p.value} has ${c} open positions` : `${p.value} has open positions`
        case 'field':
          return c ? `Find ${c} ${p.value.toLowerCase()} jobs in Finland` : `Find ${p.value.toLowerCase()} jobs in Finland`
        case 'remoteness': {
          const remote = p.rawValue.toLowerCase() === 'remote' ? 'remote' : p.value.toLowerCase()
          return c ? `Browse ${c} ${remote} jobs in Finland` : `Browse ${remote} jobs in Finland`
        }
        case 'requiredLanguages':
          return c
            ? `Find ${c} ${capitalize(p.value)}-speaking jobs in Finland`
            : `Find ${capitalize(p.value)}-speaking jobs in Finland`
        case 'jobType':
          return c ? `Browse ${c} ${p.value} jobs in Finland` : `Browse ${p.value} jobs in Finland`
        default:
          return c ? `Browse ${c} open jobs in Finland` : `Browse open jobs in Finland`
      }
    case 'fi':
      switch (p.type) {
        case 'location': {
          const loc = getCityInessive(p.rawValue)
          return c ? `Selaa ${c} avointa työpaikkaa ${loc}` : `Selaa avointa työpaikkaa ${loc}`
        }
        case 'company':
          return c ? `${p.value} tarjoaa ${c} avointa paikkaa` : `${p.value} tarjoaa avointa paikkaa`
        case 'field': {
          const gen = getFieldGenitive(p.value)
          return c ? `Löydä ${c} ${gen.toLowerCase()} työpaikkaa Suomessa` : `Löydä ${gen.toLowerCase()} työpaikkaa Suomessa`
        }
        case 'remoteness': {
          const isRemote = p.rawValue.toLowerCase() === 'remote' || p.rawValue.toLowerCase() === 'etätyö'
          return isRemote
            ? (c ? `Selaa ${c} etätyöpaikkaa Suomessa` : `Selaa etätyöpaikkaa Suomessa`)
            : (c ? `Selaa ${c} avointa paikkaa Suomessa` : `Selaa avointa paikkaa Suomessa`)
        }
        case 'requiredLanguages': {
          const adj = getFinnishLanguageAdjective(p.value)
          return c
            ? `Löydä ${c} ${adj.toLowerCase()} työpaikkaa Suomessa`
            : `Löydä ${adj.toLowerCase()} työpaikkaa Suomessa`
        }
        default:
          return c ? `Selaa ${c} avointa työpaikkaa Suomessa` : `Selaa avointa työpaikkaa Suomessa`
      }
    case 'sv':
      switch (p.type) {
        case 'location': {
          const loc = getSwedishCityPrep(p.rawValue)
          return c ? `Bläddra bland ${c} lediga jobb ${loc}` : `Bläddra bland lediga jobb ${loc}`
        }
        case 'company':
          return c ? `${p.value} har ${c} lediga tjänster` : `${p.value} har lediga tjänster`
        case 'field':
          return c ? `Hitta ${c} ${p.value.toLowerCase()}-jobb i Finland` : `Hitta ${p.value.toLowerCase()}-jobb i Finland`
        case 'remoteness': {
          const isRemote = p.rawValue.toLowerCase() === 'remote' || p.rawValue.toLowerCase() === 'distans'
          const lbl = isRemote ? 'distans' : p.value.toLowerCase()
          return c ? `Bläddra bland ${c} ${lbl}jobb i Finland` : `Bläddra bland ${lbl}jobb i Finland`
        }
        case 'requiredLanguages': {
          const adj = getSwedishLanguageAdjective(p.value)
          return c ? `Hitta ${c} ${adj.toLowerCase()} jobb i Finland` : `Hitta ${adj.toLowerCase()} jobb i Finland`
        }
        case 'jobType':
          return c ? `Bläddra bland ${c} ${p.value.toLowerCase()}-jobb i Finland` : `Bläddra bland ${p.value.toLowerCase()}-jobb i Finland`
        default:
          return c ? `Bläddra bland ${c} lediga jobb i Finland` : `Bläddra bland lediga jobb i Finland`
      }
  }
}

function salaryFact(salary: number, lang: SRPLanguage): string {
  const s = fmtSalary(salary)
  switch (lang) {
    case 'en': return `average salary €${s}/mo`
    case 'fi': return `keskipalkka €${s}/kk`
    case 'sv': return `genomsnittslön €${s}/mån`
  }
}

function listFact(lbl: string, items: string[]): string {
  return `${lbl}: ${items.join(', ')}`
}

function pctFact(pct: number, lbl: string): string {
  return `${pct}% ${lbl}`
}

function rangeFact(rangeLabel: string, lang: SRPLanguage): string {
  switch (lang) {
    case 'en': return `salary range ${rangeLabel}`
    case 'fi': return `palkkahaarukka ${rangeLabel}`
    case 'sv': return `lönespann ${rangeLabel}`
  }
}

function ctaText(lang: SRPLanguage): string {
  switch (lang) {
    case 'en': return 'Filter by salary, language and remote work on JobCrawls.'
    case 'fi': return 'Suodata palkan, kielen ja etätyön mukaan JobCrawlsissa.'
    case 'sv': return 'Filtrera efter lön, språk och distansarbete på JobCrawls.'
  }
}

/** Extra clauses when facets/hits are sparse so meta length still meets {@link MIN_DESCRIPTION_LENGTH}. */
function extraDescriptionClauses(lang: SRPLanguage): string[] {
  switch (lang) {
    case 'en':
      return [
        'Refine by industry, skills and employer.',
        'Listings reflect open roles from companies hiring in Finland.',
      ]
    case 'fi':
      return [
        'Tarkenna toimialan, osaamisen ja työnantajan mukaan.',
        'Ilmoitukset kokoavat avoimet roolit suomalaisilta työnantajilta.',
      ]
    case 'sv':
      return [
        'Förfina efter bransch, kompetens och arbetsgivare.',
        'Annonserna samlar lediga roller från företag som rekryterar i Finland.',
      ]
  }
}

function label(key: string, lang: SRPLanguage): string {
  const labels: Record<string, Record<SRPLanguage, string>> = {
    employers: { en: 'top employers',  fi: 'työnantajat',      sv: 'arbetsgivare' },
    cities:    { en: 'top cities',     fi: 'kaupungit',        sv: 'städer' },
    fields:    { en: 'top fields',     fi: 'toimialat',        sv: 'branscher' },
    english:   { en: 'of roles in English', fi: 'rooleista englanniksi', sv: 'av roller på engelska' },
    remote:    { en: 'remote or hybrid',    fi: 'etä- tai hybridityötä', sv: 'distans eller hybrid' },
    fulltime:  { en: 'full-time',           fi: 'kokoaikatyötä',         sv: 'heltid' },
    skills:    { en: 'popular skills', fi: 'suositut taidot',  sv: 'populära kompetenser' },
    benefits:  { en: 'common benefits', fi: 'yleisimmät edut', sv: 'vanliga förmåner' },
  }
  return labels[key]?.[lang] || key
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

export function sortByPriority(filters: ActiveFilter[]): ActiveFilter[] {
  return [...filters].sort(
    (a, b) => FILTER_PRIORITY.indexOf(a.type) - FILTER_PRIORITY.indexOf(b.type),
  )
}

export function fmtCount(n: number): string {
  return n.toLocaleString('en-US')
}

export function fmtSalary(n: number): string {
  if (n >= 1000 && n % 1000 === 0) return `${n / 1000}k`
  return n.toLocaleString('en-US')
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1)
}

/** Truncate title to fit ~65 chars, preserving " | JobCrawls" suffix */
function trunc(title: string): string {
  if (title.length <= MAX_TITLE_LENGTH) return title

  const suffix = ' | JobCrawls'
  const content = title.replace(suffix, '')
  const maxContent = MAX_TITLE_LENGTH - suffix.length

  const dashIdx = content.lastIndexOf(' – ')
  if (dashIdx > 20 && dashIdx <= maxContent) {
    return content.substring(0, dashIdx) + suffix
  }

  const trimmed = content.substring(0, maxContent)
  const lastSpace = trimmed.lastIndexOf(' ')
  if (lastSpace > 20) {
    return trimmed.substring(0, lastSpace) + suffix
  }

  return trimmed + suffix
}

/** Truncate description at sentence boundary, or word boundary as fallback.
 *  Never truncates below MIN_DESCRIPTION_LENGTH to preserve the length guarantee. */
function truncDesc(text: string, maxLen: number): string {
  if (text.length <= maxLen) return text

  const cut = text.substring(0, maxLen)
  const lastPeriod = cut.lastIndexOf('.')
  if (lastPeriod >= MIN_DESCRIPTION_LENGTH) {
    return cut.substring(0, lastPeriod + 1)
  }

  const lastSpace = cut.lastIndexOf(' ')
  if (lastSpace > MIN_DESCRIPTION_LENGTH) {
    return cut.substring(0, lastSpace) + '...'
  }

  return cut + '...'
}
